import React, { useEffect } from 'react';

const WIDTHS = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

const TONES = {
  brand:  'bg-brand-500/15 border-brand-400/25 text-brand-200',
  danger: 'bg-red-500/15 border-red-500/25 text-red-300',
  warn:   'bg-amber-500/15 border-amber-500/25 text-amber-300',
};

/**
 * Közös modal-váz: elsötétített, elmosott háttér + emelt lap.
 *
 * A háttérre kattintás és az Escape egyaránt zár. A tartalom kattintása nem
 * bugyborékol fel, így a lapon belül nyugodtan lehet kattintani.
 */
export default function Modal({
  title,
  subtitle,
  icon,
  tone = 'brand',
  size = 'md',
  onClose,
  children,
  footer,
  closeOnBackdrop = true,
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);

    // A háttér ne görögjön a modal alatt.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 animate-fade-in"
      onClick={closeOnBackdrop ? onClose : undefined}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

      <div
        onClick={e => e.stopPropagation()}
        className={`relative w-full ${WIDTHS[size] || WIDTHS.md} max-h-[90vh] overflow-y-auto
                    rounded-2xl bg-gray-900/95 backdrop-blur-2xl border border-white/[0.09]
                    shadow-modal animate-scale-in`}
      >
        {/* Felső fényvonal — ettől emelkedik ki a lap a háttérből */}
        <span className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

        {(title || onClose) && (
          <div className="flex items-start gap-3 p-5 pb-4">
            {icon && (
              <span className={`flex-shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center
                                ${TONES[tone] || TONES.brand}`}>
                {icon}
              </span>
            )}
            <div className="flex-1 min-w-0">
              {title && <h3 className="font-display text-base font-semibold text-white">{title}</h3>}
              {subtitle && <p className="text-sm text-gray-500 mt-1 leading-relaxed">{subtitle}</p>}
            </div>
            {onClose && (
              <button onClick={onClose} className="btn-icon -mt-1 -mr-1 flex-shrink-0" aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" className="w-4 h-4">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {children && <div className="px-5 pb-5">{children}</div>}

        {footer && (
          <div className="px-5 py-4 border-t border-white/[0.06] bg-black/20 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
